/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-self-assign */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import DefendantAppealRequestForm from "pages/InstituteOfCharge/modal/DefendantAppealRequestForm";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  AppealListParams,
  fetchDefendantAppealRequest,
  fetchDefendantAppealRequestByAdminCoordinator,
  fetchDefendantAppealRequestByAssignedProsecutor,
} from "services/appealService";
import { DefendantAppeal, DefendantAppealStatus, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";

const StatusBadge: React.FC<{ status: string; t: (key: string) => string }> = ({
  status,
  t,
}) => {
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      assigned_to_clerk: "bg-blue-100 text-blue-800 border-blue-200",
      forwarded_to_coordinator:
        "bg-purple-100 text-purple-800 border-purple-200",
      completed: "bg-green-100 text-green-800 border-green-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {DefendantAppealStatus[status as keyof typeof DefendantAppealStatus] ||
        status}
    </span>
  );
};

const DefendantAppealList: React.FC<{ isCassation?: boolean }> = ({
  isCassation,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [activeTab, setActiveTab] = useState(t("all"));

  const [defendantAppeals, setDefendantAppeals] = useState<DefendantAppeal[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [openDefendantForm, setOpenDefendantForm] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const PAGE_SIZE = 20;
  const navigate = useNavigate();

  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);

  const isVariousCoordinator = hasRole(loginData?.roles, [
    Roles.variousCoordinator,
  ]);
  const isVariousDirector = hasRole(loginData?.roles, [Roles.variousDirector]);
  const isVariousRegistrar = hasRole(loginData?.roles, [
    Roles.variousRegistrar,
  ]);
  const isVariousAppealCoordinator = hasRole(loginData?.roles, [
    Roles.variousAppealCoordinator,
  ]);

  const isEconomicCoordinator = hasRole(loginData?.roles, [
    Roles.economicCoordinator,
  ]);
  const isEconomicDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
  ]);
  const isEconomicRegistrar = hasRole(loginData?.roles, [
    Roles.economicRegistrar,
  ]);
  const isEconomicAppealCoordinator = hasRole(loginData?.roles, [
    Roles.economicAppealCoordinator,
  ]);

  const isCorruptionCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionCoordinator,
  ]);
  const isCorruptionDirector = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
  ]);
  const isCorruptionRegistrar = hasRole(loginData?.roles, [
    Roles.corruptionRegistrar,
  ]);
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);

  const isChildWomenCoordinator = hasRole(loginData?.roles, [
    Roles.childWomenCoordinator,
  ]);
  const isChildAppealCoordinator = hasRole(loginData?.roles, [
    Roles.childAppealCoordinator,
  ]);

  const selectedAdminRole = isVariousCoordinator
    ? Roles.variousCoordinator
    : // Directors see the same appeals as their crime-type appeal coordinator.
      isVariousDirector
      ? Roles.variousAppealCoordinator
      : isVariousRegistrar
        ? Roles.variousRegistrar
        : isVariousAppealCoordinator
          ? Roles.variousAppealCoordinator
          : isEconomicCoordinator
            ? Roles.economicCoordinator
            : isEconomicDirector
              ? Roles.economicAppealCoordinator
              : isEconomicRegistrar
                ? Roles.economicRegistrar
                : isEconomicAppealCoordinator
                  ? Roles.economicAppealCoordinator
                  : isCorruptionCoordinator
                    ? Roles.corruptionCoordinator
                    : isCorruptionDirector
                      ? Roles.corruptionAppealCoordinator
                      : isCorruptionRegistrar
                        ? Roles.corruptionRegistrar
                        : isCorruptionAppealCoordinator
                          ? Roles.corruptionAppealCoordinator
                          : isChildWomenCoordinator
                            ? Roles.childWomenCoordinator
                            : isChildAppealCoordinator
                              ? Roles.childAppealCoordinator
                              : null;

  // Status filter tabs — All + the 4 key lifecycle stages.
  const STATUS_TAB_KEYS = [
    "PENDING",
    "ASSIGNED_TO_PROSECUTOR",
    "FORWARDED_TO_COURT",
    "COMPLETED",
  ] as const;
  const STATUS_TABS: { label: string; status: string | null }[] = [
    { label: t("all"), status: null },
    ...STATUS_TAB_KEYS.map((key) => ({
      label: t(`defendant_appeal_status_${key.toLowerCase()}`, {
        defaultValue: DefendantAppealStatus[key],
      }),
      status: key,
    })),
  ];

  const fetchDefendantAppealData = () => {
    setLoading(true);
    const selectedStatus = STATUS_TABS.find((s) => s.label === activeTab)
      ?.status;
    const params: AppealListParams = {
      status: selectedStatus || undefined,
      search: debouncedSearch || undefined,
      page,
      pageSize: PAGE_SIZE,
      // Appeal list shows non-cassations; cassation list shows cassations.
      is_casations: isCassation,
    };

    const api = hasRole(loginData?.roles, [Roles.prosecutor])
      ? // Prosecutor sees only the defendant appeals/cassations assigned to them.
        fetchDefendantAppealRequestByAssignedProsecutor(
          loginData?.user_id + "",
          params,
        )
      : selectedAdminRole
        ? fetchDefendantAppealRequestByAdminCoordinator(
            loginData?.admin_unit + "",
            selectedAdminRole,
            params,
          )
        : fetchDefendantAppealRequest(params);
    api
      .then((res) => {
        const serverPaginated =
          res.next != null ||
          res.previous != null ||
          res.count > res.results.length;

        const scoped = (res.results || []).filter((appeal) =>
          isCassation
            ? (appeal as any)?.is_casations
            : !(appeal as any)?.is_casations,
        );

        if (serverPaginated) {
          setDefendantAppeals(scoped);
          setCount(res.count);
        } else {
          // Fallback: filter + paginate client-side.
          let rows = scoped;
          if (selectedStatus) {
            rows = rows.filter((a) => a.status === selectedStatus);
          }
          const term = debouncedSearch.trim().toLowerCase();
          if (term) {
            rows = rows.filter((a) =>
              [
                a?.higher_court_type,
                a?.court_appeal_number,
                a?.court_zone,
                a?.court_woreda,
                a?.status,
              ]
                .map((v) => (v ?? "").toString().toLowerCase())
                .some((v) => v.includes(term)),
            );
          }
          setCount(rows.length);
          setDefendantAppeals(
            rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
          );
        }
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setDefendantAppeals([]);
        setCount(0);
        setLoading(false);
      });
  };

  // Debounce the search box; reset to the first page on a new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (loginData && activeTab) {
      fetchDefendantAppealData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loginData, page, debouncedSearch]);

  const totalPages = Math.ceil(count / PAGE_SIZE);
  const filtered = defendantAppeals;

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={
          isCassation
            ? `${t("defendant")} ${t("cassation")}`
            : t("appeal")
        }
        icon={FaBriefcase}
        tabs={STATUS_TABS.map((s) => s.label)}
        activeTab={activeTab}
        onTabClick={(tab) => {
          setActiveTab(tab);
          setPage(1);
        }}
        rightContent={
          <>
            {/* Cassation is requested from the appeal's report tab, not
                registered fresh here — so the create button is appeal-only. */}
            {isRegistrar && !isCassation && (
              <button
                type="button"
                className="px-5 mt-7 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700"
                onClick={() => setOpenDefendantForm(true)}
              >
                +&nbsp;&nbsp;{t("register_defendant_appeal")}
              </button>
            )}
          </>
        }
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_appeal")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("appeal_list_table")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">
                    {activeTab === t("prosecutor_appeal")
                      ? t("type")
                      : t("court_type")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === t("prosecutor_appeal")
                      ? t("crime_reference_no")
                      : t("court_appeal_number")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === t("prosecutor_appeal")
                      ? t("incident_reg_no")
                      : t("court_zone")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === t("prosecutor_appeal")
                      ? t("court_reg_no")
                      : t("court_woreda")}
                  </th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((appeal, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {appeal?.higher_court_type}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {(appeal as DefendantAppeal)?.court_appeal_number}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {(appeal as DefendantAppeal)?.court_zone}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {(appeal as DefendantAppeal)?.court_woreda}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={appeal?.status || ""} t={t} />
                        {(appeal as any)?.is_casations && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                            ⚖️ {t("cassation")}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="p-3 font-[500] font-[Poppins] flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigate(
                            isCassation
                              ? `/defendant-cassation/${appeal?.id}`
                              : `/defendant-appeal/${appeal?.id}`,
                          );
                        }}
                        className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        {t("view")}
                      </button>
                      {(appeal as any)?.parent_appeal ||
                      (appeal as any)?.defendant_appeal_request_id ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-yellow-100 text-yellow-800 border border-yellow-300"
                          title={t("has_parent_appeal")}
                          aria-label={t("has_parent_appeal")}
                        >
                          ★
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_appeal_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {!loading && count > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {t("showing_page", {
                  defaultValue: `Page ${page} of ${Math.max(totalPages, 1)} · ${count} total`,
                  page,
                  total: Math.max(totalPages, 1),
                  count,
                })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ◀ {t("previous", { defaultValue: "Previous" })}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t("next", { defaultValue: "Next" })} ▶
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {openDefendantForm && (
        <DefendantAppealRequestForm
          isCassation={isCassation}
          onClose={(refetch) => {
            setOpenDefendantForm(false);
            if (refetch) fetchDefendantAppealData();
          }}
        />
      )}
    </div>
  );
};

export default DefendantAppealList;
